import { useState, MouseEvent } from 'react'
import { useQueryClient } from 'react-query'
import { useSetState } from 'react-use'
import { useCreateRightInvocationNote } from 'api/rightInvocationsV2/mutations/useCreateRightInvocationNote'
import { useDeleteRightInvocationNote } from 'api/rightInvocationsV2/mutations/useDeleteRightInvocationNote'
import { useUpdateRightInvocationNote } from 'api/rightInvocationsV2/mutations/useUpdateRightInvocationNote'
import { showToast } from 'components/modals/AlertComponent'
import { RightInvocationDTO, RightInvocationNoteDTO } from '@ketch-com/figurehead'
import { ApiQueryKeys } from 'api/common/queryKeys'

type MenuState = {
  anchorEl: HTMLElement | null
  selectedNoteItem: RightInvocationNoteDTO | null
}

export const useNoteManagement = (rightInvocation: RightInvocationDTO | null) => {
  const queryClient = useQueryClient()

  const [menuState, setMenuState] = useState<MenuState>({ anchorEl: null, selectedNoteItem: null })
  const [{ isNoteModalOpen, selectedNote }, setState] = useSetState({ isNoteModalOpen: false, selectedNote: '' })

  const createNote = useCreateRightInvocationNote({
    onSuccess: () => {
      queryClient.invalidateQueries([ApiQueryKeys.rightInvocationV2])
      showToast({ content: 'Changes saved successfully', type: 'success' })
    },
    onError: () => {
      showToast({ content: 'Unable to save changes', type: 'error' })
      setState({ isNoteModalOpen: false })
    },
  })

  const updateNote = useUpdateRightInvocationNote({
    onSuccess: () => {
      queryClient.invalidateQueries([ApiQueryKeys.rightInvocationV2])
      showToast({ content: 'Changes saved successfully', type: 'success' })
    },
    onError: () => {
      showToast({ content: 'Unable to save changes', type: 'error' })
    },
  })

  const deleteNote = useDeleteRightInvocationNote({
    onSuccess: () => {
      queryClient.invalidateQueries([ApiQueryKeys.rightInvocationV2])
      showToast({ content: 'Successfully deleted', type: 'success' })
    },
    onError: () => {
      showToast({ content: 'Unable to delete', type: 'error' })
    },
  })

  const handleSubmitNote = (note: string) => {
    if (!rightInvocation?.id) return

    const params = { rightInvocationID: rightInvocation.id, formData: { note } }

    if (selectedNote && menuState.selectedNoteItem?.id) {
      updateNote.mutate({
        params: {
          noteID: menuState.selectedNoteItem.id,
          ...params,
        },
      })
    } else {
      createNote.mutate({ params })
    }

    setMenuState({ anchorEl: null, selectedNoteItem: null })
    setState({ isNoteModalOpen: false, selectedNote: '' })
  }

  const handleMoreMenuClick = (event: MouseEvent<HTMLElement>, noteItem: RightInvocationNoteDTO) => {
    event.stopPropagation()
    setMenuState({ anchorEl: event.currentTarget, selectedNoteItem: noteItem })
  }

  const handleEditNote = () => {
    if (menuState.selectedNoteItem) {
      setState({
        isNoteModalOpen: true,
        selectedNote: menuState.selectedNoteItem.note,
      })
    }
    setMenuState(prev => ({ ...prev, anchorEl: null }))
  }

  const handleDeleteNote = () => {
    if (!rightInvocation?.id || !menuState.selectedNoteItem?.id) return

    deleteNote.mutate({
      params: {
        rightInvocationID: rightInvocation.id,
        noteID: menuState.selectedNoteItem.id,
      },
    })
    setMenuState({ anchorEl: null, selectedNoteItem: null })
  }

  return {
    isNoteModalOpen,
    selectedNote,
    menuState,
    setState,
    setMenuState,
    handleSubmitNote,
    handleMoreMenuClick,
    handleEditNote,
    handleDeleteNote,
  }
}
